import ApiClient from "@/utils/ApiClient";
import { PaginatedApiResponse } from "@/types/api.types";
import {
  UserResponse,
  UserListQueryParams,
} from "@/types/user-management.types";
import {
  AnnouncementListItem,
  AnnouncementStatus,
  AnnouncementApiItem,
  AnnouncementsResponseRaw,
  StatisticsPayload,
  StatisticsResponseRaw,
  DeliveryApiItem,
  DeliveriesResponseRaw,
  AnnouncementDetailsApi,
} from "@/types/system-announcements.types";

export const SystemAnnouncementsService = {
  /**
   * Fetch all users that can be targeted by a system announcement.
   *
   * Currently this is a simple wrapper around the `/user-management` GET endpoint.
   * The exact response shape can be adjusted later once the backend contract
   * for announcements targeting is finalized.
   */
  fetchAllUsers: async (
    params?: Partial<UserListQueryParams>,
  ): Promise<PaginatedApiResponse<UserResponse[]>> => {
    const response = await ApiClient.get("/user-management", {
      params,
    });

    return response.data as PaginatedApiResponse<UserResponse[]>;
  },
  listAnnouncements: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: AnnouncementStatus;
  }): Promise<PaginatedApiResponse<AnnouncementListItem[]>> => {
    const response = await ApiClient.get("/admin/system-announcements", {
      params,
    });

    const raw = response.data as AnnouncementsResponseRaw;

    const items: AnnouncementApiItem[] = Array.isArray(raw.data)
      ? (raw.data as AnnouncementApiItem[])
      : [];

    const normalizeItem = (item: AnnouncementApiItem): AnnouncementListItem => {
      const id = item.id ?? "";
      const title = item.title ?? "";

      const urgencyRaw = item.urgency;
      const urgencyLevel =
        typeof urgencyRaw === "number"
          ? (urgencyRaw as AnnouncementListItem["urgencyLevel"])
          : undefined;

      const statusRaw = item.status;
      const status =
        typeof statusRaw === "number"
          ? (statusRaw as AnnouncementStatus)
          : (AnnouncementStatus.ACTIVE as AnnouncementStatus);

      const endTime = item.ends_at ?? item.endsAt;
      const scheduledAt = item.starts_at ?? item.startsAt;
      const createdAt = item.created_at ?? item.createdAt ?? "";

      return {
        id,
        title,
        urgencyLevel: (urgencyLevel ??
          0) as AnnouncementListItem["urgencyLevel"],
        status,
        endTime,
        scheduledAt,
        createdAt,
      };
    };

    const normalizedData = items.map((item) => normalizeItem(item));

    return {
      currentPage: raw.currentPage ?? raw.page,
      pageLimit: raw.pageLimit ?? raw.limit,
      totalItems: raw.totalItems ?? raw.total,
      totalPages: raw.totalPages,
      total_items: raw.total_items,
      total: raw.total,
      current_page: raw.current_page,
      total_pages: raw.total_pages,
      data: normalizedData,
    } as PaginatedApiResponse<AnnouncementListItem[]>;
  },
  getAnnouncementStatistics: async (id: string) => {
    const response = await ApiClient.get(
      `/admin/system-announcements/${id}/statistics`,
    );
    const raw = response.data as StatisticsResponseRaw;
    const payload: StatisticsPayload = raw.data ?? {};

    return {
      total:
        payload.total_recipients ?? payload.total ?? payload.stats?.total ?? 0,
      pending: payload.pending ?? payload.stats?.pending ?? 0,
      // use `sent` as delivered aggregate
      delivered:
        payload.sent ??
        // fallback keys if backend adds them later
        (payload as { delivered?: number }).delivered ??
        payload.stats?.delivered ??
        0,
      failed: payload.failed ?? payload.stats?.failed ?? 0,
    } as {
      total: number;
      pending: number;
      delivered: number;
      failed: number;
    };
  },
  listAnnouncementDeliveries: async (
    id: string,
    params?: { page?: number; limit?: number; search?: string },
  ) => {
    const response = await ApiClient.get(
      `/admin/system-announcements/${id}/deliveries`,
      { params },
    );

    const raw = response.data as DeliveriesResponseRaw;

    const items: DeliveryApiItem[] = Array.isArray(raw.data) ? raw.data : [];

    type NormalizedDelivery = {
      id: string;
      name: string;
      email: string;
      status: string;
      deliveredAt: string | null;
    };

    const normalizeDelivery = (item: DeliveryApiItem): NormalizedDelivery => {
      const user = item.user ?? {};
      const name = (user.name as string | null | undefined) ?? item.name ?? "";
      const email =
        (user.email as string | null | undefined) ?? item.email ?? "";

      // Derive a simple status string from flags
      // We currently support: pending, delivered, failed
      let status = "pending";
      const emailFailed = Boolean(item.email_failed);
      const notifFailed = Boolean(item.notification_failed);
      const emailSent = Boolean(item.email_sent);
      const notifSent = Boolean(item.notification_sent);

      if (emailFailed || notifFailed) {
        status = "failed";
      } else if (emailSent || notifSent) {
        status = "delivered";
      }

      const deliveredAt =
        (item.email_sent_at as string | null | undefined) ??
        (item.notification_sent_at as string | null | undefined) ??
        null;

      return {
        id:
          (item.id as string | undefined) ??
          (user.id as string | undefined) ??
          "",
        name,
        email,
        status,
        deliveredAt,
      };
    };

    const normalized: NormalizedDelivery[] = items.map((item) =>
      normalizeDelivery(item),
    );

    return {
      currentPage: raw.page ?? raw.currentPage,
      pageLimit: raw.limit ?? raw.pageLimit,
      totalItems: raw.total ?? raw.totalItems ?? normalized.length,
      totalPages: raw.totalPages ?? raw.total_pages,
      data: normalized,
    } as PaginatedApiResponse<NormalizedDelivery[]>;
  },
  getAnnouncement: async (id: string): Promise<AnnouncementDetailsApi> => {
    const response = await ApiClient.get(`/admin/system-announcements/${id}`);
    const raw = response.data as
      | { status?: boolean; message?: string; data?: AnnouncementDetailsApi }
      | AnnouncementDetailsApi;

    if (raw && typeof raw === "object" && "data" in raw) {
      return ((raw as { data?: AnnouncementDetailsApi }).data ??
        {}) as AnnouncementDetailsApi;
    }

    return (raw as AnnouncementDetailsApi) ?? ({} as AnnouncementDetailsApi);
  },
  updateAnnouncement: async (
    id: string,
    payload: {
      title: string;
      message: string;
      starts_at?: string;
      ends_at: string;
      target_user_roles?: string[];
      target_user_ids?: string[];
      urgency: number;
      delivery_type: ("email" | "in-app")[];
    },
  ) => {
    const response = await ApiClient.put(
      `/admin/system-announcements/${id}`,
      payload,
    );
    return response.data;
  },
  deleteAnnouncement: async (id: string) => {
    const response = await ApiClient.delete(
      `/admin/system-announcements/${id}`,
    );
    return response.data;
  },
  createAnnouncement: async (payload: {
    title: string;
    message: string;
    starts_at?: string;
    ends_at: string;
    target_user_roles?: string[];
    target_user_ids?: string[];
    urgency: number;
    delivery_type: ("email" | "in-app")[];
  }) => {
    const response = await ApiClient.post(
      "/admin/system-announcements",
      payload,
    );
    return response.data;
  },
};
